import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Toaster } from './components/ui/sonner';
import Login from './components/Login';
import AdminDashboard from './components/AdminDashboard';
import ColaboradorDashboard from './components/ColaboradorDashboard';
import ParceriaDashboardPage from './components/ParceriaDashboardPage';
import TabErrorBoundary from './components/TabErrorBoundary';
import { authAPI } from './services/api';
import './App.css';

// Auto-recover de chunk errors após deploys: se um chunk lazy falhar a
// carregar (404 — index.html novo aponta para hashes diferentes), recarregamos
// automaticamente. Throttling: só auto-reload 1× cada 30s (evita loops).
if (typeof window !== 'undefined') {
  const onChunkError = (msg) => {
    const isChunkError = /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported|Loading CSS chunk/i.test(String(msg || ''));
    if (!isChunkError) return;
    const last = parseInt(sessionStorage.getItem('__chunk_reload_at__') || '0', 10);
    const now = Date.now();
    if (now - last < 30_000) return; // já tentámos recentemente
    sessionStorage.setItem('__chunk_reload_at__', String(now));
    setTimeout(() => window.location.reload(), 50);
  };
  window.addEventListener('error', (e) => onChunkError(e?.message || e?.error?.message));
  window.addEventListener('unhandledrejection', (e) => onChunkError(e?.reason?.message || e?.reason));
}

const ClienteApp = lazy(() => import('./components/ClienteApp'));
const SitePublico = lazy(() => import('./components/Site/SitePublico'));

// SEMPRE usar a origem atual do browser para garantir compatibilidade com produção
const BACKEND_URL = window.location.origin;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isClienteApp, setIsClienteApp] = useState(false);
  const [isSitePublico, setIsSitePublico] = useState(false);

  useEffect(() => {
    const path = window.location.pathname;
    const hash = window.location.hash;
    const hostname = (window.location.hostname || '').toLowerCase();

    // --- Routing rules (path-based, agnóstico ao domínio) ---
    // /app/*    → ClienteApp (app móvel cliente)
    // /gestao/* → Login → AdminDashboard (painel interno)
    // /*        → SitePublico (site principal — comportamento por defeito)
    //
    // Nota: o hostname-based routing antigo deixou de fazer sentido —
    // agora basta o path para distinguir os 3 modos.

    const isAppPath = path === '/app' || path.startsWith('/app/') || hash.startsWith('#/app');
    const isGestaoPath = path === '/gestao' || path.startsWith('/gestao/')
      || path === '/login' || path.startsWith('/login/');

    if (isAppPath) {
      setIsClienteApp(true);
      setLoading(false);
      return;
    }
    if (isGestaoPath) {
      // Cai no fluxo de checkAuth() abaixo (Login + Admin)
      checkAuth();
      return;
    }
    // Default: site público
    setIsSitePublico(true);
    setLoading(false);
  }, []);

  const checkAuth = async () => {
    // Primeiro, verificar se há token na URL (para login automático em nova aba)
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('auth');
    
    if (tokenFromUrl) {
      // Token veio da URL - salvar no localStorage
      localStorage.setItem('token', tokenFromUrl);
      
      // Limpar URL para não expor o token
      window.history.replaceState({}, document.title, window.location.pathname + '?tab=' + urlParams.get('tab'));
    }
    
    // Verificar token (do localStorage ou da URL)
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await authAPI.getMe();
        const userData = response.data;
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Client App route
  if (isClienteApp) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="w-10 h-10 border-3 border-teal-500 border-t-transparent rounded-full animate-spin" /></div>}>
        <ClienteApp />
        <Toaster position="top-center" richColors />
      </Suspense>
    );
  }

  // Public website route
  if (isSitePublico) {
    return (
      <Suspense fallback={<div className="min-h-screen bg-white flex items-center justify-center"><div className="w-10 h-10 border-3 border-[#376a37] border-t-transparent rounded-full animate-spin" /></div>}>
        <SitePublico />
        <Toaster position="top-center" richColors />
      </Suspense>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <div className="text-xl font-display">Carregando...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="App">
        <Login onLogin={handleLogin} />
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  return (
    <div className="App">
      <TabErrorBoundary>
      {user.role === 'admin_parceria' || user.role === 'colaborador_parceria' ? (
        <ParceriaDashboardPage user={user} onLogout={handleLogout} />
      ) : user.role === 'admin' || user.role === 'responsavel' ? (
        <AdminDashboard user={user} onLogout={handleLogout} />
      ) : (
        <ColaboradorDashboard user={user} onLogout={handleLogout} />
      )}
      </TabErrorBoundary>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
